<template>
  <SrpModal :isVisible="props.isVisible" size="medium" className="nearby-suggestion-edit-modal" :isClosable="!busy" :isCloseButtonVisible="!busy" @update:isVisible="emit('update:isVisible', $event)">
    <template #header><h2 class="global-h2">Edit suggestion</h2></template>
    <template #content>
      <div v-if="editing" class="nearby-suggestion-edit ui form">
        <label>Name <input v-model.trim="editing.displayName" /></label>
        <label>Website <input v-model.trim="editing.websiteUri" type="url" /></label>
        <label v-if="editing.tagKind === 'Location'">Address <input v-model.trim="editing.address" /></label>
        <label>Description <textarea v-model="editing.excludeFromIndex.description" rows="3" /></label>
        <template v-if="editing.excludeFromIndex.event">
          <label>Dates or typical timing <input v-model="editing.excludeFromIndex.event.date" /></label>
          <label>Event source <input v-model.trim="editing.excludeFromIndex.event.url" type="url" /></label>
        </template>
        <p v-if="error" class="global-error-string">{{ error }}</p>
      </div>
    </template>
    <template #footer>
      <SrpButton color="gray" fill="outlined" :isDisabled="busy" @click="emit('update:isVisible', false)">Cancel</SrpButton>
      <SrpButton :isDisabled="busy || !editing?.displayName?.trim()" @click="save">Save changes</SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import NearbyLocationsRepo from "@repos/NearbyLocationsRepo";
import { nearbyErrorMessage } from "@logic/NearbySuggestions";
import type { NearbySuggestion } from "@contracts/nearbyLocations";

const props = withDefaults(defineProps<{ isVisible: boolean; customerId: string; suggestion?: NearbySuggestion | null }>(), { suggestion: null });
const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "saved", suggestion: NearbySuggestion): void;
}>();

/** Working copy, so cancelling leaves the list untouched. */
const editing = ref<NearbySuggestion | null>(null);
const busy = ref(false);
const error = ref("");

watch(
  () => [props.isVisible, props.suggestion?.id] as const,
  ([visible]) => {
    if (!visible || !props.suggestion) return;
    error.value = "";
    editing.value = JSON.parse(JSON.stringify(props.suggestion));
    // Events keep their description on the embedded calendar draft.
    if (editing.value?.excludeFromIndex.event) editing.value.excludeFromIndex.description = editing.value.excludeFromIndex.event.description;
  },
  { immediate: true }
);

async function save() {
  const row = editing.value;
  if (!row || busy.value) return;
  busy.value = true;
  error.value = "";
  try {
    const saved = await NearbyLocationsRepo.update(props.customerId, row.id, {
      eTag: row._etag,
      displayName: row.displayName,
      websiteUri: row.websiteUri,
      formattedAddress: row.address,
      description: row.excludeFromIndex.description,
      eventDate: row.excludeFromIndex.event?.date,
      eventUrl: row.excludeFromIndex.event?.url,
    });
    emit("update:isVisible", false);
    emit("saved", saved);
  } catch (caught) {
    error.value = nearbyErrorMessage(caught);
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped>
.nearby-suggestion-edit { display: grid; gap: 15px; }
.nearby-suggestion-edit label { display: grid; gap: 6px; font-weight: 600; }
.nearby-suggestion-edit input, .nearby-suggestion-edit textarea { box-sizing: border-box; width: 100%; max-width: 100%; }
.nearby-suggestion-edit textarea { min-height: 80px; }
.nearby-suggestion-edit p { margin: 0; }
</style>

<style>
.nearby-suggestion-edit-modal .srp-modal__content { box-sizing: border-box; width: 100%; }
</style>
